import { useMemo } from 'react'
import LocalStorageService from '../services/local-storage.service'
import { useFetchAssignmentsQuery } from '../store/apis/assignment.api'
import { ASSIGNMENT_SORTING_TYPES } from '../constants/sorting-types'

const MAX_RECENT_ASSIGNMENTS = 5

/**
 * Hook to get recently accessed assignments.
 * First tries localStorage, falls back to fetching recent assignments from API.
 * @returns {{ recentAssignments: Array, isFetching: boolean, isFromLocalStorage: boolean }}
 */
const useRecentAssignments = () => {
    const localStorageRecents = LocalStorageService.getRecentAssignments()
    const hasLocalRecents = localStorageRecents.length > 0

    const { data, isFetching } = useFetchAssignmentsQuery(
        {
            sort: ASSIGNMENT_SORTING_TYPES.NEW_TO_OLD.value,
            pageSize: MAX_RECENT_ASSIGNMENTS,
        },
        { skip: hasLocalRecents },
    )

    const recentAssignments = useMemo(() => {
        if (hasLocalRecents) {
            return localStorageRecents
        }

        if (data?.assignments) {
            return data.assignments.map(assignment => ({
                token: assignment.token,
                name: assignment.assignment_name,
                language: assignment.language,
                layerId: assignment.current_version?.default_layer_id,
            }))
        }

        return []
    }, [hasLocalRecents, localStorageRecents, data?.assignments])

    return {
        recentAssignments,
        isFetching: !hasLocalRecents && isFetching,
        isFromLocalStorage: hasLocalRecents,
    }
}

export default useRecentAssignments

